<template>
  <div class="w-full h-full flex items-center justify-center relative">
    <input
      type="file"
      :accept="acceptedFilesTypes.join(',')"
      v-bind="inputProperties"
      @change="saveNewPicture"
      :disabled="disabled"
      :class="{
        'pointer-events-none': disabled
      }"
      class="absolute w-full h-full opacity-0 cursor-pointer"
      ref="filePicker"
    />
    <slot>
      <div class="flex gap-1 items-center justify-between w-full px-2">
        <span
          block
          overflowEllipsis
          class="w-full"
          :class="{
            'text-primary-400': !fileName
          }"
        >
          {{ fileName || placeHolder }}
        </span>
        <div
          class="buttonComponent small"
          :elasticHover="false"
          buttonClasses="m-0 "
          type="none"
          @click.prevent="
            () => {
              selectFile();
            }
          "
          :loading="savingFile"
          :disabled="savingFile"
        >
          Sélectionner
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { savePicture } from './savePicture';
import Commons from '../../libraries/commons';

export default defineComponent({
  name: 'PictureUploader',
  components: {},
  props: {
    acceptedFilesTypes: {
      type: Array,
      default: () => {
        return ['*'];
      }
    },
    inputProperties: {
      type: Object,
      default: () => {
        return {};
      }
    },
    fileId: {
      type: String,
      default: ''
    },
    placeHolder: {
      type: String,
      default: ''
    },
    canUpload: {
      type: Boolean,
      default: true
    },
    makeLoadingModal: {
      type: Boolean,
      default: true
    },
    exactTypeMatch: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxFileSize: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      savingFile: false,
      fileName: '',
      stopUploadingProgress: () => {}
    };
  },
  mounted() {},
  methods: {
    selectFile() {
      if (!this.canUpload) return;
      this.$refs.filePicker.click();
    },
    async saveNewPicture(event) {
      const [file] = event.target.files;
      if (!file) {
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Echec',
          body: 'Aucune image sélectionnée'
        });
        return;
      }
      if (!this.fileAllowed(file)) {
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Upload Annulé',
          body: "Le fichier sélectionné n'est pas autorisé"
        });
        return;
      }
      if (this.maxFileSize && file.size > this.parseMaxFileSize()) {
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Fichier trop volumineux',
          body: `La taille du fichier ne doit pas dépasser ${this.maxFileSize}`
        });
        return;
      }
      if (this.savingFile) {
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Attention',
          body: 'Veuillez patienter pendant la sauvegarde du fichier précédent'
        });
        return;
      }
      this.$emit('willUpload');
      this.emitProgressiveUpload(file);
      this.uploadFile(file);
    },
    emitProgressiveUpload(file) {
      this.stopUploadingProgress = Commons.infiniteLoading((progress) => {
        // console.log(`Progrès: ${progress.toFixed(2)}%`)
        this.$emit('uploading', {
          progress: progress.toFixed(2),
          ...this.fileDetails(file)
        });
      });
    },
    async uploadFile(file) {
      this.savingFile = true;
      if (this.makeLoadingModal) {
        this.$Events.dispatchEvent('start_loading_dismissable', {
          title: 'Sauvegarde du contenu',
          subTitle:
            'Veuillez patienter pendant la sauvegarde de votre document',
          cancelText: 'Annuler'
        });
      } else {
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Sauvegarde en cours',
          body: 'Veuillez patienter pendant la sauvegarde de votre document'
        });
      }
      const pictureSavingResponse = await savePicture(file);
      this.savingFile = false;
      this.stopUploadingProgress();
      if (this.makeLoadingModal) {
        this.$Events.dispatchEvent('finish_loading');
      }
      if (!pictureSavingResponse.success) {
        this.$bus.emit(this.$constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Erreur de sauvegarde',
          body: "Echec, nos serveur n'arrivent pas à récupérer le ficher, veuillez réessayer"
        });
        this.$emit('uploadFailed');
        return;
      }
      const fileUrl = pictureSavingResponse.data.fileUrl;
      this.fileName = file.name;
      const fileId =
        pictureSavingResponse.data.id + pictureSavingResponse.data.ext;
      this.$emit('onFileChanged', fileId, file.name, fileUrl);
      this.$emit('onFileChangedV2', {
        ...this.fileDetails(file),
        id: fileId,
        url: fileUrl
      });
      console.log('emmit onFileChanged');
    },
    fileDetails(file) {
      if (!file) {
        console.error('Aucun fichier fourni.');
        return {};
      }

      const fileDetails = {
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`, // Conversion en MB
        type: file.type || 'Type inconnu',
        lastModifiedDate: file.lastModified
          ? new Date(file.lastModified).toLocaleString()
          : 'Non disponible'
      };

      return fileDetails;
    },
    fileAllowed(file) {
      if (this.exactTypeMatch)
        return this.acceptedFilesTypes.includes(file.type);
      return true;
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' o';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' Ko';
      return (bytes / (1024 * 1024)).toFixed(2) + ' Mo';
    },
    parseMaxFileSize() {
      if (!this.maxFileSize) return null;
      if (typeof this.maxFileSize === 'number') return this.maxFileSize;

      const str = String(this.maxFileSize).toLowerCase().trim();
      const match = str.match(/^([\d.]+)\s*(o|ko|mo|go|kb|mb|gb)?$/);
      if (!match) return null;

      const value = parseFloat(match[1]);
      const unit = match[2] || 'o';

      switch (unit) {
        case 'ko':
        case 'kb':
          return value * 1024;
        case 'mo':
        case 'mb':
          return value * 1024 * 1024;
        case 'go':
        case 'gb':
          return value * 1024 * 1024 * 1024;
        default:
          return value;
      }
    }
  },
  computed: {}
});
</script>
